import type { CapRule } from './caps.js';
import { parseTimeOfDay, type MessageClass, type Tightening } from './window.js';

/**
 * Turns the platform's state rules and a workspace's overrides into the tightenings and caps for one
 * merchant (PLAN.md §4.6). Pure. Every rule of every state that applies to the merchant (the address state
 * and the area code's state when they differ) and every matching override is included; the window and cap
 * code then intersects them, so the strictest rule always wins and nothing can loosen the base rules.
 */

/** A `state_rules` row (times as Postgres returns them, "HH:MM:SS"). */
export interface StateRuleRow {
  state: string;
  name: string;
  timezones: readonly string[];
  allowedStart: string | null;
  allowedEnd: string | null;
  sundayAllowed: boolean;
  sundayStart: string | null;
  maxPer24h: number | null;
  capAppliesTo: readonly MessageClass[];
  verifiedByCounsel: boolean;
  sourceUrl: string | null;
  notes: string | null;
}

/** A `workspace_state_overrides` row; `state` null applies to every state. */
export interface OverrideRow {
  state: string | null;
  allowedStart: string | null;
  allowedEnd: string | null;
  sundayAllowed: boolean;
  maxPer24h: number | null;
  capAppliesTo: readonly MessageClass[];
}

export interface MerchantRules {
  tightenings: Tightening[];
  caps: CapRule[];
}

function stateLabel(row: StateRuleRow): string {
  return row.name;
}

export function overrideLabel(
  row: Pick<OverrideRow, 'state'>,
  stateNames?: ReadonlyMap<string, string>,
): string {
  if (row.state === null) return 'Workspace: all states';
  return `Workspace: ${stateNames?.get(row.state) ?? row.state}`;
}

export function assembleRules(
  states: readonly string[],
  stateRules: ReadonlyMap<string, StateRuleRow>,
  overrides: readonly OverrideRow[],
): MerchantRules {
  const tightenings: Tightening[] = [];
  const caps: CapRule[] = [];
  const names = new Map([...stateRules.values()].map((r) => [r.state, r.name]));
  for (const state of states) {
    const row = stateRules.get(state);
    if (!row) throw new RangeError(`no state rule for ${state}`);
    tightenings.push({
      label: stateLabel(row),
      start: parseTimeOfDay(row.allowedStart),
      end: parseTimeOfDay(row.allowedEnd),
      sundayAllowed: row.sundayAllowed,
      sundayStart: parseTimeOfDay(row.sundayStart),
    });
    if (row.maxPer24h !== null) {
      caps.push({ label: stateLabel(row), max: row.maxPer24h, appliesTo: row.capAppliesTo });
    }
  }
  for (const o of overrides) {
    if (o.state !== null && !states.includes(o.state)) continue;
    const label = overrideLabel(o, names);
    tightenings.push({
      label,
      start: parseTimeOfDay(o.allowedStart),
      end: parseTimeOfDay(o.allowedEnd),
      sundayAllowed: o.sundayAllowed,
      sundayStart: null,
    });
    if (o.maxPer24h !== null) caps.push({ label, max: o.maxPer24h, appliesTo: o.capAppliesTo });
  }
  return { tightenings, caps };
}
